/* Iveraco ring builder. Setting > Diamond > Complete.

   Step 1 is where the ring itself is specified: the customer picks the
   metal and the side stone style, then the setting that carries them.
   Step 2 is the centre stone. Step 3 is size, engraving and the bag.
   Metal and side stone are choices, not just grid filters: they are held
   in state, shown in the step summary, and sent as line item properties.

   Step 1 is server-rendered; this file filters it, draws steps 2 and 3,
   keeps state in sessionStorage, and adds the setting variant to the bag
   with the chosen diamond as line item properties.
   The centre stone inventory is real, loaded from assets/iveraco-stones.json;
   see the inventory block below for why it is a committed file. */
(function init(root) {
  'use strict';
  /* The theme editor re-renders this section whenever a setting changes,
     which replaces the markup this script wired up at page load. Without
     booting again on shopify:section:load the builder sat dead in the
     customizer until a reload. $ and $$ are scoped to root, so a fresh boot
     only ever touches the new markup; the booted flag stops a double run. */
  if (!init.listening) {
    init.listening = true;
    document.addEventListener('shopify:section:load', function (e) { init(e.target.querySelector('[data-iv-rb]')); });
  }
  if (!root || root.dataset.rbBooted) return;
  root.dataset.rbBooted = '1';

  var CUR = root.dataset.currency || 'USD';
  var fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: CUR, maximumFractionDigits: 0 });
  var money = function (cents) { return fmt.format(Math.round(cents / 100)); };

  /* ------------------------------------------------------------------ */
  /* Shapes. The svg is now the fallback: the picker, cards and detail show a
     photograph of a real stone and reveal this drawing only if it fails. */
  var SHAPES = {
    round:    { label: 'Round',    svg: '<svg viewBox="0 0 32 32"><circle cx="16" cy="16" r="13"/><path d="M16 3v26M3 16h26M6.8 6.8l18.4 18.4M25.2 6.8L6.8 25.2"/><circle cx="16" cy="16" r="6"/></svg>' },
    oval:     { label: 'Oval',     svg: '<svg viewBox="0 0 32 32"><ellipse cx="16" cy="16" rx="10" ry="14"/><path d="M16 2v28M6 16h20M9 6l14 20M23 6L9 26"/><ellipse cx="16" cy="16" rx="4.5" ry="6.5"/></svg>' },
    pear:     { label: 'Pear',     svg: '<svg viewBox="0 0 32 32"><path d="M16 2c6 6 11 12 11 18a11 11 0 0 1-22 0c0-6 5-12 11-18z"/><path d="M16 2v29M5 20h22M8 12l16 16M24 12L8 28"/></svg>' },
    cushion:  { label: 'Cushion',  svg: '<svg viewBox="0 0 32 32"><rect x="4" y="4" width="24" height="24" rx="7"/><path d="M4 16h24M16 4v24M8 8l16 16M24 8L8 24"/><rect x="10" y="10" width="12" height="12" rx="3"/></svg>' },
    emerald:  { label: 'Emerald',  svg: '<svg viewBox="0 0 32 32"><path d="M9 3h14l6 6v14l-6 6H9l-6-6V9z"/><path d="M11 7h10l4 4v10l-4 4H11l-4-4V11z"/><path d="M13 11h6l2 2v6l-2 2h-6l-2-2v-6z"/></svg>' },
    marquise: { label: 'Marquise', svg: '<svg viewBox="0 0 32 32"><path d="M16 2c8 6 10 10 10 14s-2 8-10 14C8 24 6 20 6 16S8 8 16 2z"/><path d="M16 2v28M6 16h20M10 8l12 16M22 8L10 24"/></svg>' },
    princess: { label: 'Princess', svg: '<svg viewBox="0 0 32 32"><rect x="4" y="4" width="24" height="24"/><path d="M4 4l24 24M28 4L4 28M16 4v24M4 16h24"/><rect x="10" y="10" width="12" height="12"/></svg>' },
    radiant:  { label: 'Radiant',  svg: '<svg viewBox="0 0 32 32"><path d="M9 3h14l6 6v14l-6 6H9l-6-6V9z"/><path d="M3 9l26 14M29 9L3 23M16 3v26M3 16h26"/><path d="M11 8h10l3 3v10l-3 3H11l-3-3V11z"/></svg>' },
    heart:    { label: 'Heart',    svg: '<svg viewBox="0 0 32 32"><path d="M16 29S3 19 3 11a6.5 6.5 0 0 1 13-2 6.5 6.5 0 0 1 13 2c0 8-13 18-13 18z"/><path d="M16 9v20M5 14l11 15M27 14L16 29"/></svg>' },
    asscher:  { label: 'Asscher',  svg: '<svg viewBox="0 0 32 32"><path d="M9 3h14l6 6v14l-6 6H9l-6-6V9z"/><path d="M3 9l26 14M29 9L3 23"/><path d="M12 8h8l4 4v8l-4 4h-8l-4-4v-8z"/><path d="M15 11h2l4 4v2l-4 4h-2l-4-4v-2z"/></svg>' }
  };
  var SHAPE_KEYS = Object.keys(SHAPES);
  var COLOURS = ['D', 'E', 'F', 'G', 'H', 'I', 'J'];
  var CLARITY = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1'];
  var CUTS = ['Ideal', 'Excellent', 'Very Good'];

  /* ------------------------------------------------------------------ */
  /* CENTRE STONE INVENTORY. Real stones, from Jash's IJ DIAM feed.
     ------------------------------------------------------------------
     The seeded data that used to live here is gone. STONES now loads from
     assets/iveraco-stones.json, which is generated by
     research/verify-rig/sync-stones.js and committed to the theme.

     Why a committed file and not a fetch of the API:
       - The feed is served over HTTP. The storefront is HTTPS, so a browser
         refuses it as mixed content. The API is IJ DIAM's, so a certificate
         is not ours to add.
       - The feed has no filtering: shape=, limit= and page= are ignored and
         it returns the full 7.7 MB every time, about 8 seconds. Not something
         to put in front of a shopper even if the scheme were right.
     Cleaned and committed it is roughly 100 KB, served from Shopify's CDN
     over HTTPS with Brotli, and it keeps working when their server does not.

     The object shape is unchanged, so filters, sort, cards, detail and cart
     all read exactly what they read before. Prices are integer CENTS in USD,
     the store's currency, and are PLACEHOLDERS until Jash confirms the real
     formula; see the pricing seam in assets/iveraco-diamond-feed.js. The file
     also carries shapeSamples, one photographed stone per cut for the picker. */
  var STONES = [];
  var SAMPLES = {};
  var viewing = null, pendingStone = null, urlWantsDiamond = false, booting = true;
  var stonesReady = null;

  function loadStones() {
    if (stonesReady) return stonesReady;
    var url = root.dataset.stonesUrl;
    if (!url) { stonesReady = Promise.resolve([]); return stonesReady; }
    stonesReady = fetch(url, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) {
        STONES = (data && data.stones) || [];
        SAMPLES = (data && data.shapeSamples) || {};
        return STONES;
      })
      .catch(function (e) {
        if (window.console) console.warn('[iveraco] stone inventory failed to load:', e.message);
        STONES = [];
        return STONES;
      });
    return stonesReady;
  }
  /* Start the fetch now, not when step 2 opens. A setting has to be chosen
     first, so by the time the stones are needed the file is normally
     already here and the step draws at once. */
  loadStones();
  /* ------------------------------------------------------------------ */
  /* State */
  var KEY = 'iv-rb';
  var state = { setting: null, product: null, metal: 'White Gold', sideStone: '', diamond: null, size: '', engraving: '', step: 'setting', filters: null };
  try { var saved = JSON.parse(sessionStorage.getItem(KEY) || 'null'); if (saved) state = Object.assign(state, saved); } catch (e) {}
  function save() { try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {} }

  var $ = function (sel, el) { return (el || root).querySelector(sel); };
  var $$ = function (sel, el) { return Array.prototype.slice.call((el || root).querySelectorAll(sel)); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
  var withArticle = function (w) { return (/^[aeiou]/i.test(w) ? 'an ' : 'a ') + w; };

  /* Wording. Every string the customer reads is a field in the section's
     settings, handed over as JSON in [data-rb-copy]. The second argument is
     the standard wording: it is used if the JSON is missing and, for labels
     and buttons, when a field is emptied, so an empty field can never leave
     an empty button. copyNote() is for the optional notes, which an emptied
     field hides instead. fillIn() swaps [placeholders] for live values. */
  var COPY = {};
  try { var copyEl = root.querySelector('[data-rb-copy]'); if (copyEl) COPY = JSON.parse(copyEl.textContent) || {}; } catch (e) { COPY = {}; }
  var copy = function (k, d) { var v = COPY[k]; return typeof v === 'string' && v.trim() ? v : d; };
  var copyNote = function (k, d) { if (!(k in COPY)) return d; return typeof COPY[k] === 'string' ? COPY[k].trim() : ''; };
  var fillIn = function (tpl, map) { return String(tpl).replace(/\[([a-z]+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; }); };

  /* A photograph of the stone with the line icon behind it. The icon is in
     the DOM but hidden and shown only if the image fails, so a slow or
     missing photo degrades to the drawing rather than to a blank. */
  function stonePhoto(src, svg, attrs, eager) {
    if (!src) return '<span class="iv-rb__fallback">' + svg + '</span>';
    return '<img src="' + esc(src) + '" alt="" decoding="async" loading="' + (eager ? 'eager' : 'lazy') + '"' + (attrs || '') +
      ' onerror="this.hidden=true;if(this.nextElementSibling)this.nextElementSibling.hidden=false">' +
      '<span class="iv-rb__fallback" hidden>' + svg + '</span>';
  }
  /* Frame a stone photograph so it is shown whole, upright and at one size.

     The photographs are 360-viewer stills. In 1750 of the 2540 the stone runs
     to the top and bottom edge of the file, so cropping the file to a square
     cut the stone or jammed it against the frame: a blind critic counted 9 of
     12 cut or touching. Every backdrop measured flat, corner to corner, so the
     frame is simply painted in that photograph's own backdrop colour and the
     picture is placed inside it smaller. The seam cannot show because both
     sides of it are the same colour. The sync supplies m, the file's size,
     backdrop and the box the stone occupies, and everything here is a
     percentage of the frame, so it holds at any card width.

     The stone's long side is set to 84% of the frame for long cuts and 78%
     for round and square ones, which is what the reference measures at. It is
     capped at one source pixel per device pixel at 2x, so a small file is
     drawn smaller rather than stretched. A long stone photographed lying down
     is stood up, turning about the stone's own centre.

     Two studios shot these, one backdrop near rgb(159,160,168) and one near
     rgb(208,207,204). Levelling them fully was tried and rejected: it bleached
     the stones. brightness() here only leans each family a little toward the
     other and is held to 1.10 and 0.93 for that reason. */
  var LONG_CUT = { oval: 1, pear: 1, marquise: 1, emerald: 1, radiant: 1 };
  function stoneFrame(s, boxPx, eager) {
    var svg = SHAPES[s.shape].svg, m = s.m;
    if (!s.image) return '<span class="iv-rb__fallback">' + svg + '</span>';
    if (!m) return stonePhoto(s.image, svg, '', eager);
    var w = m[0], h = m[1], sw = (m[7] - m[5]) / 1000 * w, sh = (m[8] - m[6]) / 1000 * h, side = Math.max(sw, sh);
    var cx = (m[5] + m[7]) / 2000, cy = (m[6] + m[8]) / 2000;
    var fill = Math.min(LONG_CUT[s.shape] ? 0.84 : 0.78, side / (2 * (boxPx || 254)));
    var wp = 100 * fill * w / side;
    var turn = LONG_CUT[s.shape] && sw > sh * 1.15;
    var lum = (m[2] + m[3] + m[4]) / 3, k = lum < 185 ? Math.min(1.10, 176 / lum) : Math.max(0.93, 192 / lum);
    return '<span class="iv-rb__plate" style="background:rgb(' + m[2] + ',' + m[3] + ',' + m[4] + ');filter:brightness(' + k.toFixed(3) + ') saturate(.85)">' +
      '<img src="' + esc(s.image) + '" alt="" decoding="async" loading="' + (eager ? 'eager' : 'lazy') + '" width="' + w + '" height="' + h + '"' +
      ' style="width:' + wp.toFixed(2) + '%;height:auto;left:' + (50 - cx * wp).toFixed(2) + '%;top:' + (50 - cy * wp * h / w).toFixed(2) + '%;' +
      'transform-origin:' + (cx * 100).toFixed(2) + '% ' + (cy * 100).toFixed(2) + '%' + (turn ? ';transform:rotate(90deg)' : '') + '"' +
      ' onerror="this.hidden=true;if(this.nextElementSibling)this.nextElementSibling.hidden=false">' +
      '<span class="iv-rb__fallback" hidden>' + svg + '</span></span>';
  }

  /* Shape picker media: a real stone of that cut from our own inventory,
     chosen by the sync for being typical of the shape. The feed's photos
     share one grey studio ground, so the ten read as a matched set. */
  function shapeMedia(k) {
    var src = SAMPLES && SAMPLES[k] && SAMPLES[k].image;
    return '<span class="iv-rb__shape-media">' + stonePhoto(src, SHAPES[k].svg, ' width="44" height="44"', true) + '</span>';
  }

  /* ------------------------------------------------------------------ */
  /* Steps */
  var stepBtns = $$('[data-rb-goto]');

  /* Which steps are reachable is a function of state, not of navigation.
     This lived inside setStep(), so choosing a setting left step 2 disabled
     until something else happened to call setStep: the customer picked a
     setting and the only way forward was greyed out. Now any state change
     calls refreshSteps() and the ribbon always matches what has been chosen. */
  var STEP_ORDER = ['setting', 'diamond', 'complete'];
  function refreshSteps(current) {
    var idx = STEP_ORDER.indexOf(current || state.step);
    stepBtns.forEach(function (b) {
      var i = STEP_ORDER.indexOf(b.dataset.rbGoto);
      b.classList.toggle('is-current', i === idx);
      b.classList.toggle('is-done', (i === 0 && !!state.setting) || (i === 1 && !!state.diamond));
      b.disabled = (i === 1 && !state.setting) || (i === 2 && !(state.setting && state.diamond));
    });
  }

  function setStep(name, scroll) {
    state.step = name; save();
    $$('[data-rb-panel]').forEach(function (p) { var on = p.dataset.rbPanel === name; p.hidden = !on; p.classList.toggle('is-current', on); });
    refreshSteps(name);
    if (name === 'diamond') drawDiamondStep();
    if (name === 'complete') drawComplete();
    if (scroll !== false) { var bar = $('[data-rb-steps]'); window.scrollTo({ top: bar.getBoundingClientRect().top + window.scrollY - parseFloat(getComputedStyle(root).getPropertyValue('--rb-top') || 0), behavior: 'smooth' }); }
    writeUrl();
  }

  /* The URL carries the build: ?setting=sienna-oval&shape=emerald&caratMin=1.5
     &caratMax=3&stone=LG800638246#diamond. Jash asked for every diamond and
     every ring to have its own link, and stienhardt.com keeps its filters in
     the address the same way, so a customer can send a stone to their partner
     and we can send one over WhatsApp. It used to hold only '#diamond', and
     writing that threw away any query string that was already there.
     replaceState, not pushState: filtering should not fill the back button.
     Parameters we do not own (preview_theme_id, utm tags) are left alone. */
  var DF_DEFAULT = { caratMin: 0.5, caratMax: 4, sort: 'price-asc' };
  var OUR_KEYS = ['setting', 'shape', 'caratMin', 'caratMax', 'colour', 'clarity', 'cut', 'priceMax', 'sort', 'stone'];
  function certId(cert) { return String(cert || '').replace(/^\s*[A-Za-z]+\s+/, ''); }
  function writeUrl() {
    /* Nothing is written while the page is still starting. Restoring a setting
       calls chooseSetting(), which writes the address; during start-up that ran
       before the link's own hash and filters had been read, stamped '#setting'
       over them, and a shared link opened on step 1 with its filters gone. */
    if (booting || !history.replaceState) return;
    var q = new URLSearchParams(location.search);
    OUR_KEYS.forEach(function (k) { q.delete(k); });
    if (state.setting) q.set('setting', state.setting.handle);
    if (df && state.step !== 'setting') {
      if (df.shapes.length) q.set('shape', df.shapes.join(','));
      if (df.caratMin !== DF_DEFAULT.caratMin) q.set('caratMin', df.caratMin);
      if (df.caratMax !== DF_DEFAULT.caratMax) q.set('caratMax', df.caratMax);
      if (df.colours.length) q.set('colour', df.colours.join(','));
      if (df.clarity.length) q.set('clarity', df.clarity.join(','));
      if (df.cuts.length) q.set('cut', df.cuts.join(','));
      if (df.priceMax) q.set('priceMax', df.priceMax);
      if (df.sort !== DF_DEFAULT.sort) q.set('sort', df.sort);
    }
    var st = state.diamond || (state.step === 'diamond' ? viewing : null);
    if (st) q.set('stone', certId(st.cert));
    var qs = q.toString().replace(/%2C/g, ',');
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + '#' + (state.step || 'setting'));
  }
  stepBtns.forEach(function (b) { b.addEventListener('click', function () { if (!b.disabled) setStep(b.dataset.rbGoto); }); });

  /* View and Change both land on the step, which is what the reference does:
     the difference is that Change clears the choice so the grid is browsable
     again, while View leaves it selected and scrolls to the detail panel.
     Both live inside the step button, so stop the click from bubbling into
     the button's own handler and running setStep twice. */
  $$('[data-rb-view]').forEach(function (el) {
    el.addEventListener('click', function (ev) {
      ev.stopPropagation();
      setStep(el.dataset.rbView);
    });
  });
  $$('[data-rb-change]').forEach(function (el) {
    el.addEventListener('click', function (ev) {
      ev.stopPropagation();
      var step = el.dataset.rbChange;
      if (step === 'setting') {
        state.setting = null; state.product = null;
        $$('[data-rb-card]').forEach(function (c) { c.classList.remove('is-selected'); });
        var det = $('[data-rb-detail]'); if (det) { det.hidden = true; det.innerHTML = ''; }
      }
      if (step === 'diamond') state.diamond = null;
      save(); updateSummary(); setStep(step);
    });
  });
  $('[data-rb-restart]').addEventListener('click', function () {
    state = { setting: null, product: null, metal: 'White Gold', sideStone: '', diamond: null, size: '', engraving: '', step: 'setting', filters: null }; save();
    $$('[data-rb-filter="style"] .iv-rb__chip').forEach(function (c) { c.classList.toggle('is-on', c.dataset.value === ''); });
    filt.style = '';
    applySettingFilters();
    $$('[data-rb-card]').forEach(function (c) { c.classList.remove('is-selected'); });
    var d = $('[data-rb-detail]'); d.hidden = true; d.innerHTML = '';
    updateSummary(); setStep('setting');
  });

  /* 'three-stone' -> 'Three stone', for summaries and cart properties. */
  function styleLabel(v) { return v ? String(v).replace(/-/g, ' ').replace(/^./, function (c) { return c.toUpperCase(); }) : ''; }

  function updateSummary() {
    var s = $('[data-rb-summary="setting"]'), d = $('[data-rb-summary="diamond"]'), c = $('[data-rb-summary="complete"]');

    /* The ribbon reads "Select your SETTING" until a choice is made, then
       "Your setting / CLASSIC SOLITAIRE" with View and Change under it. The
       label above the value carries the tense, so the value itself is always
       the noun: either the thing still to pick or the thing picked. */
    function label(step, text) {
      var el = $('[data-rb-steplabel="' + step + '"]');
      if (el) el.textContent = text;
    }
    function links(step, on) {
      var el = $('[data-rb-links="' + step + '"]');
      if (el) el.hidden = !on;
    }

    if (state.setting) {
      s.textContent = state.setting.title;
      label('setting', copy('step_your_setting', 'Your setting'));
      links('setting', true);
    } else {
      s.textContent = copy('step_setting', 'Setting');
      label('setting', copy('step_select', 'Select your'));
      links('setting', false);
    }

    if (state.diamond) {
      d.textContent = state.diamond.carat.toFixed(2) + 'ct ' + SHAPES[state.diamond.shape].label + ' ' + state.diamond.colour + ' ' + state.diamond.clarity;
      label('diamond', copy('step_your_stone', 'Your stone'));
      links('diamond', true);
    } else {
      d.textContent = copy('step_stone', 'Stone');
      label('diamond', copy('step_select', 'Select your'));
      links('diamond', false);
    }

    c.textContent = state.size ? copy('step_size', 'Size') + ' ' + state.size : copy('step_ring', 'Ring');
    label('complete', copy('step_complete', 'Complete your'));
    refreshSteps();
    var total = $('[data-rb-total]'), restart = $('[data-rb-restart]');
    var sum = (state.setting ? settingPrice() : 0) + (state.diamond ? state.diamond.price : 0);
    total.hidden = !state.setting; restart.hidden = !state.setting;
    total.innerHTML = '<small>' + esc(state.diamond ? copy('total_label', 'Total') : copy('step_setting', 'Setting')) + '</small>' + money(sum);
  }
  function settingPrice() {
    if (state.product) {
      var v = pickVariant(); if (v) return v.price;
    }
    return state.setting ? Number(state.setting.price) : 0;
  }
  function pickVariant() {
    if (!state.product) return null;
    var vs = state.product.variants;
    var byMetal = vs.filter(function (v) { return v.options.indexOf(state.metal) > -1; });
    var pool = byMetal.length ? byMetal : vs;
    var avail = pool.filter(function (v) { return v.available; });
    return (avail.length ? avail : pool)[0] || null;
  }

  /* ------------------------------------------------------------------ */
  /* Step 1: settings */
  var cards = $$('[data-rb-card]');
  var filt = { style: state.sideStone || '', metal: state.metal || 'White Gold' };
  function applySettingFilters() {
    var n = 0;
    cards.forEach(function (c) {
      var on = !filt.style || c.dataset.style === filt.style;
      c.hidden = !on; if (on) n++;
    });
    $('[data-rb-count]').textContent = n;
  }
  $$('[data-rb-filter="style"] .iv-rb__chip').forEach(function (chip) {
    chip.addEventListener('click', function () {
      $$('[data-rb-filter="style"] .iv-rb__chip').forEach(function (c) { c.classList.remove('is-on'); });
      chip.classList.add('is-on'); filt.style = chip.dataset.value;
      /* The side stone style is a choice, not just a filter: remember it so
         it shows in the summary and reaches the cart. */
      state.sideStone = chip.dataset.value; save();
      applySettingFilters(); updateSummary();
      if (state.setting && state.setting.style !== chip.dataset.value) {
        /* The chosen setting no longer matches the side stone asked for. */
        var box = $('[data-rb-detail]'); box.hidden = true;
      }
    });
  });
  $$('[data-rb-filter="metal"] .iv-rb__swatch').forEach(function (sw) {
    sw.classList.toggle('is-on', sw.dataset.value === filt.metal);
    sw.addEventListener('click', function () {
      $$('[data-rb-filter="metal"] .iv-rb__swatch').forEach(function (c) { c.classList.remove('is-on'); });
      sw.classList.add('is-on'); filt.metal = sw.dataset.value; state.metal = sw.dataset.value; save(); updateSummary();
      if (!$('[data-rb-detail]').hidden && state.setting) drawSettingDetail();
    });
  });

  function chooseSetting(card) {
    cards.forEach(function (c) { c.classList.toggle('is-selected', c === card); });
    state.setting = { handle: card.dataset.handle, title: card.dataset.title, price: Number(card.dataset.price), img: card.dataset.img, url: card.dataset.url, style: card.dataset.style, shape: card.dataset.shape, metals: card.dataset.metals };
    /* Picking a setting settles the side stone style even if no chip was
       used, so step 1 always yields metal + side stone + setting. */
    if (card.dataset.style) state.sideStone = card.dataset.style;
    state.product = null; save(); updateSummary(); writeUrl();
    drawSettingDetail();
    fetch(root.dataset.shopUrl ? card.dataset.url.replace(/\/?$/, '.js') : card.dataset.url + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) { if (state.setting && state.setting.handle === p.handle) { state.product = p; save(); updateSummary(); drawSettingDetail(); } })
      .catch(function () {});
  }
  cards.forEach(function (card) { $('[data-rb-pick]', card).addEventListener('click', function () { chooseSetting(card); }); });

  function drawSettingDetail() {
    var s = state.setting, box = $('[data-rb-detail]'); if (!s) return;
    var p = state.product, v = pickVariant();
    var img = s.img;
    if (p && v && v.featured_image && v.featured_image.src) img = v.featured_image.src;
    var desc = p && p.description ? p.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 220) : fillIn(copy('setting_text', 'Made to order in 14k or 18k gold, finished by hand in Surat. Holds a [shape] stone from 0.50ct.'), { shape: s.shape && s.shape !== 'multi' ? s.shape : copy('card_centre', 'centre') });
    var metals = (s.metals || '').split('|').filter(Boolean);
    box.innerHTML =
      '<div class="iv-rb__detail-inner">' +
        '<div class="iv-rb__detail-media"><img src="' + esc(img) + '" alt="' + esc(s.title) + '" width="700" height="700"></div>' +
        '<div>' +
          '<p class="iv-rb__detail-kicker">' + esc(copy('step_your_setting', 'Your setting')) + '</p>' +
          '<h2 class="iv-rb__detail-title">' + esc(s.title) + '</h2>' +
          '<p class="iv-rb__detail-price">' + money(settingPrice()) + ' <span style="color:var(--rb-mute)">' + esc(copy('price_setting', 'setting')) + ', ' + esc(state.metal.toLowerCase()) + '</span></p>' +
          '<p class="iv-rb__detail-text">' + esc(desc) + '</p>' +
          (metals.length ? '<div class="iv-rb__metals" style="margin:0 0 22px">' + metals.map(function (m) { return '<button type="button" class="iv-rb__chip' + (m === state.metal ? ' is-on' : '') + '" data-rb-metal="' + esc(m) + '">' + esc(m) + '</button>'; }).join('') + '</div>' : '') +
          '<div class="iv-rb__detail-actions">' +
            '<button type="button" class="iv-rb__btn" data-rb-continue>' + esc(copy('continue_button', 'Continue to diamond')) + '</button>' +
            '<a class="iv-rb__link" href="' + esc(s.url) + '">' + esc(copy('listing_link', 'See the full listing')) + '</a>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="iv-rb__detail-close" data-rb-close aria-label="Close"><svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M2 2l12 12M14 2L2 14"/></svg></button>';
    box.hidden = false;
    $$('[data-rb-metal]', box).forEach(function (b) { b.addEventListener('click', function () {
      state.metal = b.dataset.rbMetal; save();
      $$('[data-rb-filter="metal"] .iv-rb__swatch').forEach(function (c) { c.classList.toggle('is-on', c.dataset.value === state.metal); });
      updateSummary(); drawSettingDetail();
    }); });
    $('[data-rb-continue]', box).addEventListener('click', function () { setStep('diamond'); });
    $('[data-rb-close]', box).addEventListener('click', function () { box.hidden = true; });
    if (!box.dataset.shown) { box.dataset.shown = '1'; box.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  }

  /* ------------------------------------------------------------------ */
  /* Step 2: diamonds */
  var df = state.filters || { shapes: [], caratMin: 0.5, caratMax: 4, colours: [], clarity: [], cuts: [], priceMax: 0, sort: 'price-asc' };

  /* A link beats what the session remembered. Anything in the address is
     validated against the scales before it is believed: an unknown shape or
     grade is dropped rather than filtering the list down to nothing. */
  (function readUrl() {
    var q = new URLSearchParams(location.search), touched = false;
    function list(name, allowed, norm) {
      var v = q.get(name); if (!v) return null; touched = true;
      return v.split(',').map(function (x) { return norm(x.trim()); }).filter(function (x, i, a) { return allowed.indexOf(x) > -1 && a.indexOf(x) === i; });
    }
    var lower = function (x) { return x.toLowerCase(); }, upper = function (x) { return x.toUpperCase(); };
    var title = function (x) { return x.toLowerCase().replace(/(^|\s)\S/g, function (m) { return m.toUpperCase(); }); };
    var sh = list('shape', SHAPE_KEYS, lower); if (sh) df.shapes = sh;
    var co = list('colour', COLOURS.concat(['fancy']), function (x) { return lower(x) === 'fancy' ? 'fancy' : upper(x); }); if (co) df.colours = co;
    var cl = list('clarity', CLARITY, upper); if (cl) df.clarity = cl;
    var cu = list('cut', CUTS, title); if (cu) df.cuts = cu;
    var mn = parseFloat(q.get('caratMin')), mx = parseFloat(q.get('caratMax')), pm = parseFloat(q.get('priceMax'));
    if (isFinite(mn) && mn >= 0) { df.caratMin = mn; touched = true; }
    if (isFinite(mx) && mx > 0) { df.caratMax = mx; touched = true; }
    if (isFinite(pm) && pm > 0) { df.priceMax = pm; touched = true; }
    var so = q.get('sort'); if (so && /^(price|carat)-(asc|desc)$/.test(so)) { df.sort = so; touched = true; }
    var st = q.get('stone'); if (st) { pendingStone = st.trim(); touched = true; }
    if (touched) { state.filters = df; urlWantsDiamond = true; }
  }());
  var shown = 20, drawn = false;

  /* The stones arrive over the network now, so the step is drawn in two
     passes: the filter rail and a loading line straight away, then the table
     once the file lands. Without this the first visit renders "no stones
     match" for as long as the fetch takes, which reads as an empty shop. */
  /* A link to one stone. If it is still in the file it opens; if it has gone,
     say so plainly, as the reference does, rather than showing nothing. Jash's
     feed is live and a stone leaves it when it sells, so this will happen. */
  function openPending() {
    if (!pendingStone) return;
    var want = pendingStone.toUpperCase(); pendingStone = null;
    for (var i = 0; i < STONES.length; i++) if (certId(STONES[i].cert).toUpperCase() === want) { viewStone(STONES[i].id); return; }
    var box = $('[data-rb-diamond-detail]'); if (!box) return;
    box.innerHTML = '<p class="iv-rb__sold" role="status">' + esc(copy('sold', 'That diamond is no longer available. It may have been chosen by someone else. Every stone below is in stock now.')) + '</p>' +
      '<button type="button" class="iv-rb__detail-close" data-rb-close aria-label="Close"><svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M2 2l12 12M14 2L2 14"/></svg></button>';
    box.hidden = false;
    $('[data-rb-close]', box).addEventListener('click', function () { box.hidden = true; });
  }
  function stoneStatus(msg) {
    var empty = $('[data-rb-diamond-empty]');
    if (!empty) return;
    empty.hidden = false;
    empty.textContent = msg;
  }

  function drawDiamondStep() {
    if (STONES.length) { drawFiltersOnce(); drawStones(); openPending(); return; }

    /* The shape picker is built from photographs carried in the same file as
       the stones, so the rail waits for the file too. The fetch started at
       page load, so this normally resolves at once. */
    stoneStatus(copy('loading', 'Loading our diamonds…'));
    loadStones().then(function () {
      drawFiltersOnce();
      if (!STONES.length) {
        stoneStatus(copy('unavailable', 'Our diamond list is briefly unavailable. Please try again shortly, or book an appointment and we will show you the stones in person.'));
        return;
      }
      drawStones();
      openPending();
    });
  }

  function drawFiltersOnce() {
    if (!drawn) {
      drawn = true;
      if (state.setting && state.setting.shape && SHAPES[state.setting.shape] && !df.shapes.length) df.shapes = [state.setting.shape];
      var rail = $('[data-rb-diamond-filters]');
      var colourNote = copyNote('colour_note', 'D is colourless. G to H reads white to the eye and costs much less.');
      var clarityNote = copyNote('clarity_note', 'VS1 and up is clean to the naked eye.');
      var shapeNote = state.setting && state.setting.shape && SHAPES[state.setting.shape]
        ? fillIn(copyNote('shape_note', '[setting] is photographed with [shape]. Any shape can be set.'), { setting: state.setting.title, shape: withArticle(state.setting.shape) })
        : '';
      rail.innerHTML =
        '<div class="iv-rb__filter"><p class="iv-rb__filter-title">' + esc(copy('f_shape', 'Shape')) + '</p><div class="iv-rb__shapes">' +
          SHAPE_KEYS.map(function (k) { return '<button type="button" class="iv-rb__shape" data-shape="' + k + '" aria-pressed="false">' + shapeMedia(k) + '<span>' + SHAPES[k].label + '</span></button>'; }).join('') +
        '</div>' + (shapeNote ? '<p class="iv-rb__filter-note">' + esc(shapeNote) + '</p>' : '') + '</div>' +
        '<div class="iv-rb__filter"><p class="iv-rb__filter-title">' + esc(copy('f_carat', 'Carat')) + '</p><div class="iv-rb__range"><label>' + esc(copy('f_min', 'Min')) + '<input type="number" step="0.1" min="0.5" max="4" data-df="caratMin"></label><label>' + esc(copy('f_max', 'Max')) + '<input type="number" step="0.1" min="0.5" max="4" data-df="caratMax"></label></div></div>' +
        '<div class="iv-rb__filter"><p class="iv-rb__filter-title">' + esc(copy('f_colour', 'Colour')) + '</p><div class="iv-rb__scale" data-df-set="colours">' + COLOURS.map(function (c) { return '<button type="button" class="iv-rb__chip" data-value="' + c + '">' + c + '</button>'; }).join('') + '</div>' +
        '<div class="iv-rb__chips iv-rb__chips--after" data-df-set="colours"><button type="button" class="iv-rb__chip" data-value="fancy">' + esc(copy('fancy_colour', 'Fancy colour')) + '</button></div>' + (colourNote ? '<p class="iv-rb__filter-note">' + esc(colourNote) + '</p>' : '') + '</div>' +
        '<div class="iv-rb__filter"><p class="iv-rb__filter-title">' + esc(copy('f_clarity', 'Clarity')) + '</p><div class="iv-rb__scale" data-df-set="clarity">' + CLARITY.map(function (c) { return '<button type="button" class="iv-rb__chip" data-value="' + c + '">' + c + '</button>'; }).join('') + '</div>' + (clarityNote ? '<p class="iv-rb__filter-note">' + esc(clarityNote) + '</p>' : '') + '</div>' +
        '<div class="iv-rb__filter"><p class="iv-rb__filter-title">' + esc(copy('f_cut', 'Cut')) + '</p><div class="iv-rb__chips" data-df-set="cuts">' + CUTS.map(function (c) { return '<button type="button" class="iv-rb__chip" data-value="' + c + '">' + c + '</button>'; }).join('') + '</div></div>' +
        '<div class="iv-rb__filter"><p class="iv-rb__filter-title">' + esc(copy('f_price', 'Price up to')) + '</p><div class="iv-rb__range"><label>' + esc(copy('f_price_field', 'Diamond')) + '<input type="number" step="100" min="0" data-df="priceMax" placeholder="' + esc(copy('f_any', 'Any')) + '"></label></div></div>' +
        '<div class="iv-rb__filter"><button type="button" class="iv-rb__link" data-df-reset>' + esc(copy('clear_filters', 'Clear filters')) + '</button></div>';
      $$('.iv-rb__shape', rail).forEach(function (b) { b.addEventListener('click', function () { toggle(df.shapes, b.dataset.shape); syncFilters(); renderStones(); }); });
      $$('[data-df-set]', rail).forEach(function (grp) { $$('.iv-rb__chip', grp).forEach(function (b) { b.addEventListener('click', function () { toggle(df[grp.dataset.dfSet], b.dataset.value); syncFilters(); renderStones(); }); }); });
      $$('[data-df]', rail).forEach(function (inp) { inp.addEventListener('change', function () { var v = parseFloat(inp.value); df[inp.dataset.df] = isNaN(v) ? (inp.dataset.df === 'caratMin' ? 0.5 : inp.dataset.df === 'caratMax' ? 4 : 0) : v; syncFilters(); renderStones(); }); });
      $('[data-df-reset]', rail).addEventListener('click', function () { df = { shapes: [], caratMin: 0.5, caratMax: 4, colours: [], clarity: [], cuts: [], priceMax: 0, sort: df.sort }; syncFilters(); renderStones(); });
      $('[data-rb-sort]').value = df.sort;
      $('[data-rb-sort]').addEventListener('change', function (e) { df.sort = e.target.value; renderStones(); });
      $('[data-rb-more]').addEventListener('click', function () { shown += 20; renderStones(false); });
    }
  }

  function drawStones() {
    /* renderStones owns the empty panel from here on, so restore the text
       stoneStatus overwrote. Without this the loading line stays behind the
       results. */
    var empty = $('[data-rb-diamond-empty]');
    if (empty) empty.textContent = copy('empty', 'No stones match. Loosen a filter or two.');
    syncFilters();
    renderStones();
  }
  function toggle(arr, v) { var i = arr.indexOf(v); if (i > -1) arr.splice(i, 1); else arr.push(v); }
  /* Applied filters, shown as removable chips above the grid.
     Choosing a setting silently seeded df.shapes from the setting's own
     shape, which is the right default but was invisible: the customer saw a
     short list and no reason for it. stienhardt.com shows the same thing as
     "Shape: Oval x" and "Carat: 1.5-3 x". Every chip here removes exactly the
     filter it names, so nothing is applied that cannot be seen and undone. */
  function drawActiveFilters() {
    var host = $('[data-rb-active-filters]');
    if (!host) return;
    var chips = [];
    df.shapes.forEach(function (sh) {
      chips.push({ label: copy('f_shape', 'Shape') + ': ' + (SHAPES[sh] ? SHAPES[sh].label : sh), kind: 'shape', value: sh });
    });
    if (df.caratMin > 0.5 + 1e-9 || df.caratMax < 4 - 1e-9) {
      chips.push({ label: copy('f_carat', 'Carat') + ': ' + df.caratMin + ' ' + copy('chip_to', 'to') + ' ' + df.caratMax, kind: 'carat', value: '' });
    }
    df.colours.forEach(function (c) { chips.push({ label: copy('f_colour', 'Colour') + ': ' + (c === 'fancy' ? copy('chip_fancy', 'Fancy') : c), kind: 'colours', value: c }); });
    df.clarity.forEach(function (c) { chips.push({ label: copy('f_clarity', 'Clarity') + ': ' + c, kind: 'clarity', value: c }); });
    df.cuts.forEach(function (c) { chips.push({ label: copy('f_cut', 'Cut') + ': ' + c, kind: 'cuts', value: c }); });
    if (df.priceMax) chips.push({ label: copy('chip_under', 'Under') + ' ' + money(df.priceMax * 100), kind: 'priceMax', value: '' });

    host.hidden = !chips.length;
    if (!chips.length) { host.innerHTML = ''; return; }
    host.innerHTML = chips.map(function (c) {
      return '<button type="button" class="iv-rb__active-chip" aria-label="' + esc(copy('remove_filter', 'Remove filter')) + ' ' + esc(c.label) + '" data-rb-drop="' + esc(c.kind) + '" data-rb-dropval="' + esc(c.value) + '">' +
        esc(c.label) + '<span aria-hidden="true">&times;</span></button>';
    }).join('') + '<button type="button" class="iv-rb__link" data-rb-clear-all>' + esc(copy('clear_all', 'Clear all')) + '</button>';

    $$('[data-rb-drop]', host).forEach(function (b) {
      b.addEventListener('click', function () {
        var kind = b.dataset.rbDrop, val = b.dataset.rbDropval;
        if (kind === 'shape') toggle(df.shapes, val);
        else if (kind === 'carat') { df.caratMin = 0.5; df.caratMax = 4; }
        else if (kind === 'priceMax') df.priceMax = 0;
        else toggle(df[kind], val);
        syncFilters(); renderStones();
      });
    });
    var clr = $('[data-rb-clear-all]', host);
    if (clr) clr.addEventListener('click', function () {
      df = { shapes: [], caratMin: 0.5, caratMax: 4, colours: [], clarity: [], cuts: [], priceMax: 0, sort: df.sort };
      syncFilters(); renderStones();
    });
  }

  function syncFilters() {
    state.filters = df; save(); writeUrl();
    var rail = $('[data-rb-diamond-filters]');
    $$('.iv-rb__shape', rail).forEach(function (b) { var on = df.shapes.indexOf(b.dataset.shape) > -1; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on); });
    $$('[data-df-set]', rail).forEach(function (grp) { $$('.iv-rb__chip', grp).forEach(function (b) { b.classList.toggle('is-on', df[grp.dataset.dfSet].indexOf(b.dataset.value) > -1); }); });
    $('[data-df="caratMin"]', rail).value = df.caratMin; $('[data-df="caratMax"]', rail).value = df.caratMax;
    $('[data-df="priceMax"]', rail).value = df.priceMax || '';
    drawActiveFilters();
  }
  /* White stones carry a D to J grade; fancy colours (yellow, pink) do not.
     With no colour chosen the list shows whites only, so a $130 pink does
     not head an engagement list sorted by price. The Fancy chip brings
     them in, on their own or alongside chosen grades. */
  function colourMatch(s) {
    var wantFancy = df.colours.indexOf('fancy') > -1;
    var grades = df.colours.filter(function (c) { return c !== 'fancy'; });
    if (s.fancy) return wantFancy;
    if (grades.length) return grades.indexOf(s.colour) > -1;
    return !wantFancy;
  }
  function filtered() {
    var list = STONES.filter(function (s) {
      return (!df.shapes.length || df.shapes.indexOf(s.shape) > -1) &&
        s.carat >= df.caratMin - 1e-9 && s.carat <= df.caratMax + 1e-9 &&
        colourMatch(s) &&
        (!df.clarity.length || df.clarity.indexOf(s.clarity) > -1) &&
        (!df.cuts.length || df.cuts.indexOf(s.cut) > -1) &&
        (!df.priceMax || s.price / 100 <= df.priceMax);
    });
    var k = df.sort.split('-'), key = k[0], dir = k[1] === 'asc' ? 1 : -1;
    list.sort(function (a, b) { return (a[key] - b[key]) * dir || a.id.localeCompare(b.id); });
    return list;
  }
  function renderStones(reset) {
    if (reset !== false) shown = 20;
    var list = filtered(), body = $('[data-rb-diamond-rows]');
    $('[data-rb-diamond-count]').textContent = list.length;
    $('[data-rb-diamond-empty]').hidden = list.length > 0;
    $('[data-rb-more]').hidden = list.length <= shown;
    /* Cards, not rows. The photograph is the product, so it gets the space:
       a square of the actual stone, one line of what it is, one line of
       grade, the price. Everything else lives in the detail panel. The whole
       card is the target, one tab stop each, Enter or Space to open. */
    body.innerHTML = list.slice(0, shown).map(function (s) {
      var label = s.carat.toFixed(2) + ' ct ' + SHAPES[s.shape].label;
      var grade = (s.colour || s.fancy || '') + ' · ' + s.clarity + (s.cut ? ' · ' + s.cut : '');
      var sel = !!(state.diamond && state.diamond.id === s.id);
      return '<article class="iv-rb__stone' + (sel ? ' is-selected' : '') + '" data-rb-stone="' + esc(s.id) + '" role="button" tabindex="0" aria-pressed="' + sel + '" aria-label="' + esc(label + ', ' + grade + ', ' + money(s.price) + ', ' + s.cert) + '">' +
        '<div class="iv-rb__stone-media">' + stoneFrame(s, 254) + '</div>' +
        '<div class="iv-rb__stone-body">' +
          '<p class="iv-rb__stone-shape">' + esc(SHAPES[s.shape].label) + '</p>' +
          '<p class="iv-rb__stone-title">' + esc(s.carat.toFixed(2) + ' ct · ' + grade) + '</p>' +
          '<p class="iv-rb__stone-foot"><span class="iv-rb__stone-price">' + money(s.price) + '</span><span class="iv-rb__stone-cert">' + esc(s.cert) + '</span></p>' +
        '</div>' +
      '</article>';
    }).join('');
    $$('[data-rb-stone]', body).forEach(function (card) {
      card.addEventListener('click', function () { viewStone(card.dataset.rbStone); });
      card.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); viewStone(card.dataset.rbStone); } });
    });
  }
  function stoneById(id) { for (var i = 0; i < STONES.length; i++) if (STONES[i].id === id) return STONES[i]; return null; }
  function viewStone(id) {
    var s = stoneById(id), box = $('[data-rb-diamond-detail]'); if (!s) return;
    $$('[data-rb-stone]').forEach(function (el) { var on = el.dataset.rbStone === id; el.classList.toggle('is-selected', on); el.setAttribute('aria-pressed', on); });
    box.innerHTML =
      '<div class="iv-rb__detail-inner">' +
        '<div class="iv-rb__detail-media iv-rb__detail-media--stone">' + stoneFrame(s, 280, true) + '</div>' +
        '<div>' +
          '<p class="iv-rb__detail-kicker">' + esc(fillIn(copy('stone_kicker', 'Lab grown, [certificate]'), { certificate: s.cert })) + '</p>' +
          '<h2 class="iv-rb__detail-title">' + s.carat.toFixed(2) + ' ' + esc(copy('stone_carat', 'carat')) + ' ' + esc(SHAPES[s.shape].label) + '</h2>' +
          '<p class="iv-rb__detail-price">' + money(s.price) + '</p>' +
          '<ul class="iv-rb__spec">' +
            '<li><span>Colour</span><span>' + esc(s.colour || s.fancy) + '</span></li><li><span>Clarity</span><span>' + s.clarity + '</span></li>' +
            '<li><span>Cut</span><span>' + esc(s.cut) + '</span></li><li><span>Polish</span><span>' + s.polish + '</span></li>' +
            '<li><span>Symmetry</span><span>' + s.symmetry + '</span></li><li><span>Fluorescence</span><span>' + s.fluor + '</span></li>' +
            '<li><span>Table</span><span>' + s.table + '</span></li><li><span>Depth</span><span>' + s.depth + '</span></li>' +
            '<li><span>Measurements</span><span>' + s.dims + '</span></li><li><span>Certificate</span><span>' + esc(s.cert) + '</span></li>' +
          '</ul>' +
          (state.setting ? '<p class="iv-rb__detail-sum"><span>' + esc(fillIn(copy('sum_line', 'Setting [setting] + diamond [diamond]'), { setting: money(settingPrice()), diamond: money(s.price) })) + '</span><strong>' + money(settingPrice() + s.price) + '</strong></p>' : '') +
          '<div class="iv-rb__detail-actions"><button type="button" class="iv-rb__btn" data-rb-choose-stone>' + esc(copy('choose_stone', 'Choose this diamond')) + '</button></div>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="iv-rb__detail-close" data-rb-close aria-label="Close"><svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M2 2l12 12M14 2L2 14"/></svg></button>';
    box.hidden = false;
    viewing = s; writeUrl();
    $('[data-rb-choose-stone]', box).addEventListener('click', function () { state.diamond = s; save(); updateSummary(); setStep('complete'); });
    $('[data-rb-close]', box).addEventListener('click', function () { box.hidden = true; viewing = null; writeUrl(); });
    box.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ------------------------------------------------------------------ */
  /* Step 3: complete */
  var SIZES = []; for (var z = 4; z <= 12; z += .5) SIZES.push(String(z));
  function drawComplete() {
    var s = state.setting, d = state.diamond, box = $('[data-rb-complete]'); if (!s || !d) return;
    var v = pickVariant(), sp = settingPrice(), total = sp + d.price;
    var metals = (s.metals || '').split('|').filter(Boolean);
    var note = $('[data-rb-tpl="note"]') ? $('[data-rb-tpl="note"]').innerHTML : '';
    var sizeHelp = copyNote('size_help', 'Not sure? Leave it and we confirm your size by email before making.');
    var apptLink = copy('appointment_link', '/pages/book-appointment');
    box.innerHTML =
      '<div class="iv-rb__complete">' +
        '<div>' +
          '<p class="iv-rb__eyebrow">' + esc(copy('your_ring', 'Your ring')) + '</p>' +
          '<h2 class="iv-rb__detail-title" style="font-size:40px;margin-bottom:24px">' + esc(fillIn(copy('ring_title', '[setting] with a [stone]'), { setting: s.title, stone: d.carat.toFixed(2) + 'ct ' + SHAPES[d.shape].label.toLowerCase() })) + '</h2>' +
          '<div class="iv-rb__pair">' +
            '<figure><div class="iv-rb__detail-media"><img src="' + esc(v && v.featured_image && v.featured_image.src ? v.featured_image.src : s.img) + '" alt="" width="700" height="700"></div><figcaption><strong>' + esc(s.title) + '</strong><span>' + esc(state.metal) + ', ' + esc(copy('made_to_order', 'made to order')) + '</span><br><button type="button" class="iv-rb__link" data-rb-goto2="setting">' + esc(copy('change_setting', 'Change setting')) + '</button></figcaption></figure>' +
            '<figure><div class="iv-rb__detail-media iv-rb__detail-media--stone">' + stoneFrame(d, 300, true) + '</div><figcaption><strong>' + d.carat.toFixed(2) + 'ct ' + esc(SHAPES[d.shape].label) + '</strong><span>' + esc(d.colour || d.fancy) + ' colour, ' + d.clarity + ', ' + (d.cut ? esc(d.cut) + ' cut, ' : '') + esc(d.cert) + '</span><br><button type="button" class="iv-rb__link" data-rb-goto2="diamond">' + esc(copy('change_diamond', 'Change diamond')) + '</button></figcaption></figure>' +
          '</div>' +
        '</div>' +
        '<div class="iv-rb__order">' +
          '<h3 class="iv-rb__order-title">' + esc(copy('finish_title', 'Finish it')) + '</h3>' +
          (metals.length ? '<div class="iv-rb__field">' + esc(copy('metal_title', 'Metal')) + '<div class="iv-rb__metals">' + metals.map(function (m) { return '<button type="button" class="iv-rb__chip' + (m === state.metal ? ' is-on' : '') + '" data-rb-metal="' + esc(m) + '">' + esc(m) + '</button>'; }).join('') + '</div></div>' : '') +
          '<label class="iv-rb__field">' + esc(copy('size_field', 'Ring size (US)')) + '<select data-rb-size><option value="">' + esc(copy('size_unsure', 'Not sure yet')) + '</option>' + SIZES.map(function (z) { return '<option value="' + z + '"' + (state.size === z ? ' selected' : '') + '>' + z + '</option>'; }).join('') + '</select>' + (sizeHelp ? '<small>' + esc(sizeHelp) + '</small>' : '') + '</label>' +
          '<label class="iv-rb__field">' + esc(copy('engraving_field', 'Engraving, optional')) + '<input type="text" maxlength="20" placeholder="' + esc(copy('engraving_placeholder', 'Up to 20 characters')) + '" data-rb-engrave value="' + esc(state.engraving) + '"></label>' +
          '<ul class="iv-rb__lines">' +
            '<li><span>' + esc(copy('step_setting', 'Setting')) + ', ' + esc(state.metal.toLowerCase()) + '</span><span>' + money(sp) + '</span></li>' +
            '<li><span>' + esc(copy('line_diamond', 'Diamond')) + '</span><span>' + money(d.price) + '</span></li>' +
            '<li class="is-total"><span>' + esc(copy('total_label', 'Total')) + '</span><span>' + money(total) + '</span></li>' +
          '</ul>' +
          '<button type="button" class="iv-rb__btn" data-rb-add' + (v ? '' : ' disabled') + '>' + esc(copy('add_to_bag', 'Add to bag')) + '</button>' +
          '<a class="iv-rb__btn iv-rb__btn--ghost" href="' + esc(apptLink) + '">' + esc(copy('appointment_label', 'See it in Surat first')) + '</a>' +
          '<div class="iv-rb__note">' + note + '</div>' +
          '<p class="iv-rb__status" data-rb-status hidden></p>' +
        '</div>' +
      '</div>';
    $$('[data-rb-goto2]', box).forEach(function (b) { b.addEventListener('click', function () { setStep(b.dataset.rbGoto2); }); });
    $$('[data-rb-metal]', box).forEach(function (b) { b.addEventListener('click', function () { state.metal = b.dataset.rbMetal; save(); updateSummary(); drawComplete(); }); });
    $('[data-rb-size]', box).addEventListener('change', function (e) { state.size = e.target.value; save(); updateSummary(); });
    $('[data-rb-engrave]', box).addEventListener('input', function (e) { state.engraving = e.target.value; save(); });
    $('[data-rb-add]', box).addEventListener('click', addToBag);
  }
  function addToBag() {
    var v = pickVariant(), d = state.diamond, btn = $('[data-rb-add]'), st = $('[data-rb-status]');
    if (!v || !d) return;

    /* Checkout is held closed until the diamond pricing is confirmed.

       The feed gives a wholesale figure and Jash's rule is 100% on the per
       carat price, which lands a 1.00ct D/VS1 round near $158 where that
       grade commonly retails between $800 and $1500. Until that is confirmed
       deliberate, an order taken here would be honoured at a price well under
       the stone's cost. The rest of the builder works: the customer can pick
       a stone and see the quote, and this asks them to speak to us.

       Checkout opens with the section's "Let customers add a built ring to
       the bag" setting, which writes data-rb-checkout="open" on the root. */
    if (root.dataset.rbCheckout !== 'open') {
      st.hidden = false;
      st.className = 'iv-rb__status';
      st.textContent = copy('quote_message', 'This ring is ready to quote. Book an appointment or call us to confirm your stone and complete the order.');
      return;
    }

    btn.disabled = true; btn.textContent = copy('adding', 'Adding');
    var props = {
      'Metal': state.metal,
      'Centre stone': d.carat.toFixed(2) + 'ct ' + SHAPES[d.shape].label + ', ' + (d.colour || d.fancy) + ' ' + d.clarity + (d.cut ? ', ' + d.cut : ''),
      'Certificate': d.cert,
      'Diamond price': money(d.price),
      'Ring total quoted': money(settingPrice() + d.price),
      'Ring size': state.size || 'To confirm'
    };
    if (state.sideStone) props['Side stone'] = styleLabel(state.sideStone);
    if (state.engraving) props['Engraving'] = state.engraving;
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: props }] }) })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || 'Could not add'); return j; }); })
      .then(function () {
        st.hidden = false; st.className = 'iv-rb__status'; st.textContent = copy('added', 'Added. Taking you to your bag.');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        setTimeout(function () { window.location.href = '/cart'; }, 600);
      })
      .catch(function (e) { st.hidden = false; st.className = 'iv-rb__status is-error'; st.textContent = e.message; btn.disabled = false; btn.textContent = copy('add_to_bag', 'Add to bag'); });
  }

  /* ------------------------------------------------------------------ */
  /* Boot: restore, deep link, sticky offset under the theme header. */
  function headerOffset() {
    /* The theme's header is static in flow and becomes fixed (.stuck) once
       the page scrolls, so the offset is the header's height only then. */
    var h = document.querySelector('.m-header');
    var top = 0;
    if (h && (h.classList.contains('stuck') || h.classList.contains('header-sticky-always'))) {
      var w = h.querySelector('.m-header__wrapper') || h;
      if (getComputedStyle(w).position === 'fixed' || getComputedStyle(h).position === 'fixed' || getComputedStyle(h).position === 'sticky') top = w.getBoundingClientRect().height;
    }
    root.style.setProperty('--rb-top', Math.round(top) + 'px');
  }
  var ticking = false;
  window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(function () { headerOffset(); ticking = false; }); } }, { passive: true });
  headerOffset(); window.addEventListener('resize', headerOffset);

  var q = new URLSearchParams(location.search).get('setting');
  if (q) { var pre = cards.filter(function (c) { return c.dataset.handle === q; })[0]; if (pre) { chooseSetting(pre); state.step = 'setting'; } }
  else if (state.setting) {
    var card = cards.filter(function (c) { return c.dataset.handle === state.setting.handle; })[0];
    if (card) { card.classList.add('is-selected'); drawSettingDetail(); if (!state.product) chooseSetting(card); }
  }
  $$('[data-rb-filter="style"] .iv-rb__chip').forEach(function (c) { c.classList.toggle('is-on', c.dataset.value === (filt.style || '')); });
  applySettingFilters(); updateSummary();
  var hash = location.hash.replace('#', '');
  /* A link that names a stone or carries filters means the diamond step, once a
     setting is known, unless the link says otherwise itself. */
  if (urlWantsDiamond && state.setting && hash !== 'setting' && hash !== 'complete') hash = 'diamond';
  var start = (hash === 'diamond' && state.setting) ? 'diamond' : (hash === 'complete' && state.setting && state.diamond) ? 'complete' : (state.step && state.step !== 'setting' && state.setting ? state.step : 'setting');
  if (start === 'complete' && !state.diamond) start = 'diamond';
  booting = false;
  setStep(start, false);
})(document.querySelector('[data-iv-rb]'));
